import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { SegmentedTabs } from "@akuna/ui";
import { Button } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { BarcodeScanner } from "@/components/ui/barcode-scanner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@akuna/ui";
import { useCreateItem, useUpdateItem, useUpsertItemDoseIntervals, Item, ItemInput } from "@/hooks/useItems";
import { useItems } from "@/hooks/useItems";
import { useProductSubtypes } from "@/hooks/useProductSubtypes";
import { useServiceGroups } from "@/hooks/useServiceGroups";
import { usePackageSubtypes } from "@/hooks/usePackageSubtypes";
import {
  catalogItemSchema,
  CatalogItemInput,
} from "@/lib/validations/catalog";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { CreateProductSubtypeDialog } from "@/components/erp/CreateProductSubtypeDialog";
import { CreateServiceGroupDialog } from "@/components/erp/CreateServiceGroupDialog";
import { CreatePackageSubtypeDialog } from "@/components/erp/CreatePackageSubtypeDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ItemFiscalProfile, useItemFiscalProfile, useUpsertItemFiscalProfile } from "@/hooks/useItemFiscalProfile";
import { useAddOrUpdateProductStock, useProductStockByItem } from "@/hooks/useProductStock";
import { Label } from "@/components/ui/label";
import { Info, Lock, Plus, Trash2, Unlock } from "lucide-react";
import { useCatalogPackageTemplate, useUpsertCatalogPackageTemplate } from "@/hooks/useCatalogPackages";
import { useStayPackageConfig, useUpsertStayPackageConfig } from "@/hooks/useStayPackageConfig";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { normalizeCurrencyInput, normalizeQuantityInput, parseCurrencyInput } from "@/lib/utils";
import { toast } from "sonner";
import { Checkbox } from "@akuna/ui";
import { BEHAVIOR_OPTIONS } from "@/lib/appointments/availability";
import { DOSE_INTERVAL_UNITS, DoseIntervalUnit } from "@/lib/dose-interval";
import { Separator } from "@akuna/ui";
import { FiscalCombobox } from "@/components/erp/FiscalCombobox";
import { FocusTaxCodeAutocomplete } from "@/components/erp/FocusTaxCodeAutocomplete";
import {
  originOptions,
  icmsOptions,
  pisCofinsOptions,
  ibsCbsSituationOptions,
  ibsCbsClassOptions,
  unitOptions,
} from "@/components/erp/fiscalMockData";
import { getNfseFieldStatus, saoPauloNfseProfile } from "@/lib/fiscal/nfse/profiles";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { FocusServiceListAutocomplete } from "@/components/erp/FocusServiceListAutocomplete";
import { FocusCnaeAutocomplete } from "@/components/erp/FocusCnaeAutocomplete";
import { useClinicFiscalSettings } from "@/hooks/useClinicFiscalSettings";
import {
  buildDaycarePackageTemplateItem,
  daycarePackageFormSchema,
  toStayPackageConfigValue,
  type StayPackageConfigValue,
} from "@/lib/validations/stayPackage";

const nfseProfile = saoPauloNfseProfile;
const isNfseServiceCodeSupported =
  getNfseFieldStatus(nfseProfile, "nfse_service_code") !== "unsupported";
const isNfseCnaeSupported =
  getNfseFieldStatus(nfseProfile, "nfse_cnae") !== "unsupported";

type DoseIntervalRow = {
  id?: string;
  next_application_item_id: string;
  next_application_search_text?: string;
  dose_interval_value: string;
  dose_interval_unit: DoseIntervalUnit | "";
};

const CURRENT_ITEM_NEXT_APPLICATION_ID = "__current_item__";

const getFiscalFormValues = (profile?: ItemFiscalProfile | null): Partial<CatalogItemInput> => ({
  gtin: profile?.gtin || "",
  ncm: profile?.ncm || "",
  cest: profile?.cest || "",
  cfop: profile?.cfop || "",
  cfop_outside_state: profile?.cfop_outside_state || "",
  origin_code: profile?.origin_code || "",
  icms_tax_situation: profile?.icms_tax_situation || "",
  pis_tax_situation: profile?.pis_tax_situation || "",
  cofins_tax_situation: profile?.cofins_tax_situation || "",
  ipi_tax_situation: profile?.ipi_tax_situation || "",
  ipi_enquadramento: profile?.ipi_enquadramento || "",
  ipi_base_calculo: profile?.ipi_base_calculo ?? undefined,
  ipi_aliquota: profile?.ipi_aliquota ?? undefined,
  ipi_valor: profile?.ipi_valor ?? undefined,
  nfse_service_list_item: profile?.nfse_service_list_item || "",
  nfse_service_code: profile?.nfse_service_code || "",
  nfse_cnae: profile?.nfse_cnae || "",
  nfse_iss_rate: profile?.nfse_iss_rate ?? undefined,
  nfse_service_description: profile?.nfse_service_description || "",
  unidade_tributavel: profile?.unidade_tributavel || "",
  valor_total_tributos: profile?.valor_total_tributos ?? undefined,
  aliquota_aproximada_tributos: profile?.aliquota_aproximada_tributos ?? undefined,
  fonte_valor_total_tributos: profile?.fonte_valor_total_tributos || "",
  icms_base_calculo: profile?.icms_base_calculo ?? undefined,
  icms_aliquota: profile?.icms_aliquota ?? undefined,
  icms_valor: profile?.icms_valor ?? undefined,
  icms_modalidade_base_calculo: profile?.icms_modalidade_base_calculo || "",
  pis_base_calculo: profile?.pis_base_calculo ?? undefined,
  pis_aliquota: profile?.pis_aliquota ?? undefined,
  pis_valor: profile?.pis_valor ?? undefined,
  cofins_base_calculo: profile?.cofins_base_calculo ?? undefined,
  cofins_aliquota: profile?.cofins_aliquota ?? undefined,
  cofins_valor: profile?.cofins_valor ?? undefined,
  ibs_cbs_situacao_tributaria: profile?.ibs_cbs_situacao_tributaria || "",
  ibs_cbs_classificacao_tributaria: profile?.ibs_cbs_classificacao_tributaria || "",
  ibs_cbs_base_calculo: profile?.ibs_cbs_base_calculo ?? undefined,
  cbs_aliquota: profile?.cbs_aliquota ?? undefined,
  cbs_valor: profile?.cbs_valor ?? undefined,
  ibs_uf_aliquota: profile?.ibs_uf_aliquota ?? undefined,
  ibs_uf_valor: profile?.ibs_uf_valor ?? undefined,
  ibs_mun_aliquota: profile?.ibs_mun_aliquota ?? undefined,
  ibs_mun_valor: profile?.ibs_mun_valor ?? undefined,
  ibs_valor_total: profile?.ibs_valor_total ?? undefined,
  is_valor: profile?.is_valor ?? undefined,
});

interface CatalogItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: Item;
  /** Optional defaults to prefill the "Novo Item" form. Ignored when editing an existing item. */
  initialValues?: Partial<CatalogItemInput>;
  onProductCreated?: (productId: string) => void;
}


export function CatalogItemFormDialog({ open, onOpenChange, item, initialValues, onProductCreated }: CatalogItemFormDialogProps) {
  const { t } = useTranslation(["erp", "calendar", "common"]);
  const fiscalEnabled = useFiscalEnabled();
  const { has: hasModule } = useModuleAccess();
  const hasStock = hasModule("stock");
  const hasFinancial = hasModule("financial");
  const createItem = useCreateItem();
  const updateItem = useUpdateItem();
  const addOrUpdateStock = useAddOrUpdateProductStock();
  const upsertItemFiscalProfile = useUpsertItemFiscalProfile();
  const { data: itemFiscalProfile } = useItemFiscalProfile(item?.id, fiscalEnabled);
  const { data: productStock } = useProductStockByItem(item?.id ?? "");
  const { data: productSubtypes = [] } = useProductSubtypes({ enabled: open });
  const { data: packageSubtypes = [] } = usePackageSubtypes({ enabled: open });
  const { data: serviceGroups = [] } = useServiceGroups({ enabled: open });
  const { data: itemsData } = useItems({ isActive: true });
  const upsertCatalogPackageTemplate = useUpsertCatalogPackageTemplate();
  const { data: existingPackageTemplate } = useCatalogPackageTemplate(item?.id);
  const { data: existingStayPackageConfig } = useStayPackageConfig(existingPackageTemplate?.id);
  const upsertStayPackageConfig = useUpsertStayPackageConfig();
  const [createProductSubtypeDialogOpen, setCreateProductSubtypeDialogOpen] = useState(false);
  const [createServiceGroupDialogOpen, setCreateServiceGroupDialogOpen] = useState(false);
  const [createPackageSubtypeDialogOpen, setCreatePackageSubtypeDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [pricingDriver, setPricingDriver] = useState<"markup" | "sale" | null>(null);
  const [stockQuantityDisplay, setStockQuantityDisplay] = useState("0");
  const [isMarkupUnlocked, setIsMarkupUnlocked] = useState(false);
  const [packageControlsSessions, setPackageControlsSessions] = useState(true);
  const [packageDefaultSessions, setPackageDefaultSessions] = useState<string>("1");
  const [stayPackageKind, setStayPackageKind] = useState<StayPackageConfigValue["packageKind"]>("session_block");
  const [stayPackageBaseServiceId, setStayPackageBaseServiceId] = useState<string | null>(null);
  const [stayPackagePeriod, setStayPackagePeriod] = useState<StayPackageConfigValue["period"]>(null);
  const [stayPackageDaysPerWeek, setStayPackageDaysPerWeek] = useState<string>("");
  const [packageItems, setPackageItems] = useState<Array<{
    itemId: string;
    itemNameSnapshot: string;
    unitPriceSnapshot: string;
  }>>([
    {
      itemId: "",
      itemNameSnapshot: "",
      unitPriceSnapshot: "",
    },
  ]);

  const upsertItemDoseIntervals = useUpsertItemDoseIntervals();
  const [doseIntervalRows, setDoseIntervalRows] = useState<DoseIntervalRow[]>([]);

  const catalogItems = useMemo(() => {
    const raw = Array.isArray(itemsData) ? itemsData : itemsData?.data ?? [];
    return raw.filter((row) => (!item || row.id !== item.id) && row.type !== "PACOTE");
  }, [item, itemsData]);
  const daycareServiceOptions = useMemo<AutocompleteOption[]>(
    () => catalogItems
      .filter((catalogItem) => catalogItem.type === "SERVICO")
      .map((catalogItem) => ({ value: catalogItem.id, label: catalogItem.name })),
    [catalogItems],
  );
  const selectedDaycareService = useMemo(
    () => catalogItems.find((catalogItem) => catalogItem.id === stayPackageBaseServiceId && catalogItem.type === "SERVICO") ?? null,
    [catalogItems, stayPackageBaseServiceId],
  );
  const { data: clinicFiscalSettings } = useClinicFiscalSettings();
  const municipalityCode = clinicFiscalSettings?.codigo_municipio_emitente ?? "3550308";

  const parseLocaleNumber = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return NaN;

    // pt-BR often uses "." for thousands and "," for decimals (e.g. "1.234,56")
    if (trimmed.includes(",") && trimmed.includes(".")) {
      return Number.parseFloat(trimmed.replace(/\./g, "").replace(",", "."));
    }

    return Number.parseFloat(trimmed.replace(",", "."));
  };

  const form = useForm<CatalogItemInput>({
    resolver: zodResolver(catalogItemSchema),
    defaultValues: {
      type: "PRODUTO",
      name: "",
      sku: "",
      barcode: "",
      unit: "UN",
      cost_price: 0,
      sale_price: 0,
      description: "",
      is_active: true,
      track_by_batch: false,
      track_by_expiry: false,
      is_perishable: false,
      gtin: "",
      ncm: "",
      cest: "",
      cfop: "",
      cfop_outside_state: "",
      origin_code: "",
      icms_tax_situation: "",
      pis_tax_situation: "",
      cofins_tax_situation: "",
      ipi_tax_situation: "",
      ipi_enquadramento: "",
      ipi_base_calculo: undefined,
      ipi_aliquota: undefined,
      ipi_valor: undefined,
      nfse_service_list_item: "",
      nfse_service_code: "",
      nfse_cnae: "",
      nfse_iss_rate: undefined,
      nfse_service_description: "",
      unidade_tributavel: "",
      valor_total_tributos: undefined,
      aliquota_aproximada_tributos: undefined,
      fonte_valor_total_tributos: "",
      icms_base_calculo: undefined,
      icms_aliquota: undefined,
      icms_valor: undefined,
      icms_modalidade_base_calculo: "",
      pis_base_calculo: undefined,
      pis_aliquota: undefined,
      pis_valor: undefined,
      cofins_base_calculo: undefined,
      cofins_aliquota: undefined,
      cofins_valor: undefined,
      ibs_cbs_situacao_tributaria: "",
      ibs_cbs_classificacao_tributaria: "",
      ibs_cbs_base_calculo: undefined,
      cbs_aliquota: undefined,
      cbs_valor: undefined,
      ibs_uf_aliquota: undefined,
      ibs_uf_valor: undefined,
      ibs_mun_aliquota: undefined,
      ibs_mun_valor: undefined,
      ibs_valor_total: undefined,
      is_valor: undefined,
    },
  });

  const itemType = form.watch("type");
  const itemName = form.watch("name");
  const productSubtypeId = form.watch("product_subtype_id");
  const restrictAppointmentTypes = form.watch("restrict_appointment_types");
  const availabilityItemLabel = itemType === "SERVICO"
    ? t("erp:common.service")
    : itemType === "PACOTE"
      ? t("erp:common.package")
      : t("erp:common.product");
  const costPrice = form.watch("cost_price");
  const markupPercentage = form.watch("markup_percentage");
  const salePrice = form.watch("sale_price");
  const isPackageTypeSelected = itemType === "PACOTE";
  const namePlaceholder = useMemo(() => {
    if (itemType === "SERVICO") return t("erp:itemForm.placeholders.name.service");
    if (itemType === "PACOTE") return t("erp:itemForm.placeholders.name.package");
    return t("erp:itemForm.placeholders.name.product");
  }, [itemType, t]);
  const skuLabel = itemType === "PRODUTO" ? t("erp:itemForm.labels.sku") : t("erp:itemForm.labels.internalCode");
  const skuPlaceholder = itemType === "PRODUTO"
    ? t("erp:itemForm.placeholders.sku")
    : t("erp:itemForm.placeholders.internalCode");
  const descriptionPlaceholder = useMemo(() => {
    if (itemType === "SERVICO") return t("erp:itemForm.placeholders.descriptionService");
    if (itemType === "PACOTE") return t("erp:itemForm.placeholders.descriptionPackage");
    return t("erp:itemForm.placeholders.descriptionProduct");
  }, [itemType, t]);
  const selectedProductSubtypeName = useMemo(() => {
    if (!productSubtypeId) return null;
    return productSubtypes.find((subtype) => subtype.id === productSubtypeId)?.name?.trim().toLowerCase() ?? null;
  }, [productSubtypeId, productSubtypes]);
  const shouldShowDoseInterval = itemType === "PRODUTO" && ["vacina", "medicamento"].includes(selectedProductSubtypeName ?? "");
  const nextApplicationOptions = useMemo<AutocompleteOption[]>(
    () => {
      const selfLabel = itemName?.trim() || item?.name?.trim() || t("erp:common.item");
      const selfOption = [{
        value: CURRENT_ITEM_NEXT_APPLICATION_ID,
        label: t("erp:itemForm.doseInterval.currentItemOption", { itemName: selfLabel }),
      }];
      const otherOptions = catalogItems
        .filter((catalogItem) => {
          const subtypeName = catalogItem.product_subtype?.name?.trim().toLowerCase() ?? "";
          return catalogItem.type === "PRODUTO" && ["vacina", "medicamento"].includes(subtypeName);
        })
        .map((catalogItem) => ({ value: catalogItem.id, label: catalogItem.name }))
        .filter((option) => option.value !== item?.id);
      return [...selfOption, ...otherOptions];
    },
    [catalogItems, item?.id, item?.name, itemName, t],
  );
  const nextApplicationOptionByValue = useMemo(
    () => new Map(nextApplicationOptions.map((option) => [option.value, option])),
    [nextApplicationOptions],
  );
  const getNextApplicationDisplayText = (value: string) =>
    nextApplicationOptionByValue.get(value)?.label ?? "";

  const computedPackagePrice = useMemo(() => {
    const itemsTotal = packageItems.reduce((sum, row) => {
      const price = Number(row.unitPriceSnapshot.replace(",", "."));
      return sum + Math.max(0, Number.isFinite(price) ? price : 0);
    }, 0);
    const packageSessions = packageControlsSessions
      ? Math.max(1, Number(packageDefaultSessions.replace(",", ".")) || 1)
      : 1;
    return itemsTotal * packageSessions;
  }, [packageControlsSessions, packageItems, packageDefaultSessions]);

  useEffect(() => {
    if (isPackageTypeSelected) return;
  }, [isPackageTypeSelected]);

  useEffect(() => {
    if (!isPackageTypeSelected) return;
    form.setValue("barcode", null, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [form, isPackageTypeSelected]);

  const computedMarkupPercentage = useMemo(() => {
    if (itemType !== "PRODUTO") return undefined;
    if (costPrice == null || costPrice <= 0) return undefined;
    if (salePrice == null) return undefined;
    if (salePrice < costPrice) return undefined;
    return ((salePrice - costPrice) / costPrice) * 100;
  }, [costPrice, itemType, salePrice]);

  useEffect(() => {
    if (!open) return;
    if (itemType === "PRODUTO") {
      const defaults: Partial<CatalogItemInput> = {
        cfop: "5102",
        cfop_outside_state: "6102",
        origin_code: "0",
        icms_tax_situation: "102",
        pis_tax_situation: "07",
        cofins_tax_situation: "07",
        unidade_tributavel: form.getValues("unit") || "UN",
        ibs_cbs_situacao_tributaria: "000",
        ibs_cbs_classificacao_tributaria: "000001",
      };
      for (const [key, value] of Object.entries(defaults) as Array<[keyof CatalogItemInput, string]>) {
        if (!form.getValues(key)) {
          form.setValue(key, value, { shouldDirty: false, shouldValidate: false });
        }
      }
    }
    if (itemType === "SERVICO" && !form.getValues("nfse_service_list_item")) {
      form.setValue("nfse_service_list_item", "14.13", { shouldDirty: false, shouldValidate: false });
    }
  }, [form, itemType, open]);

  const toggleMarkupUnlocked = () => {
    if (itemType !== "PRODUTO") return;

    if (isMarkupUnlocked) {
      setIsMarkupUnlocked(false);
      setPricingDriver("sale");
      return;
    }

    setIsMarkupUnlocked(true);
    setPricingDriver("markup");
    if (computedMarkupPercentage !== undefined) {
      form.setValue("markup_percentage", Number(computedMarkupPercentage.toFixed(2)), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  };

  // Calculate sale_price from cost_price + markup (only when markup is unlocked/driver)
  useEffect(() => {
    if (itemType !== "PRODUTO") return;
    if (!isMarkupUnlocked) return;
    if (pricingDriver === "sale") return;
    if (!costPrice || markupPercentage === undefined || markupPercentage === null) return;

    if (costPrice > 0) {
      const calculated = costPrice * (1 + markupPercentage / 100);
      form.setValue("sale_price", Number(calculated.toFixed(2)), {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [costPrice, isMarkupUnlocked, itemType, markupPercentage, pricingDriver, form]);

  // Keep markup_percentage synced from cost_price + sale_price while markup is locked
  useEffect(() => {
    if (itemType !== "PRODUTO") return;
    if (isMarkupUnlocked) return;

    setPricingDriver("sale");

    const currentMarkup = form.getValues("markup_percentage");
    if (computedMarkupPercentage === undefined) {
      if (currentMarkup !== undefined && currentMarkup !== null) {
        form.setValue("markup_percentage", undefined, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
      return;
    }

    const nextMarkup = Number(computedMarkupPercentage.toFixed(2));
    if (currentMarkup !== nextMarkup) {
      form.setValue("markup_percentage", nextMarkup, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [computedMarkupPercentage, form, isMarkupUnlocked, itemType]);

  // Reset form when dialog opens/closes or item changes
  useEffect(() => {
    if (open) {
      setActiveTab("general");
      setPricingDriver(null);
      setIsMarkupUnlocked(false);
      if (item) {
        form.reset({
          type: item.type,
          name: item.name,
          sku: item.sku || "",
          barcode: item.barcode || "",
          unit: item.unit || "UN",
          cost_price: item.cost_price || 0,
          sale_price: item.sale_price || 0,
          description: item.description || "",
          is_active: item.is_active,
          track_by_batch: item.track_by_batch,
          track_by_expiry: item.track_by_expiry ?? false,
          is_perishable: item.is_perishable || false,
          product_subtype_id: item.product_subtype_id ?? undefined,
          package_subtype_id: item.package_subtype_id ?? undefined,
          service_group_id: item.service_group_id ?? undefined,
          service_duration_minutes: item.service_duration_minutes || undefined,
          manufacturer: item.manufacturer || "",
          dose_ml: item.dose_ml || undefined,
          storage_temp: item.storage_temp || "",
          registration_number: item.registration_number || "",
          restrict_appointment_types: item.allowed_appointment_types.length > 0,
          allowed_appointment_types: item.allowed_appointment_types.filter((value): value is "padrao" | "retorno" | "aplicacao" | "banho_tosa" => ["padrao", "retorno", "aplicacao", "banho_tosa"].includes(value)),
          ...getFiscalFormValues(itemFiscalProfile),
        });
        setDoseIntervalRows(
          (item.dose_intervals ?? [])
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((interval) => ({
              id: interval.id,
              next_application_item_id:
                interval.next_application_item_id && interval.next_application_item_id === item.id
                  ? CURRENT_ITEM_NEXT_APPLICATION_ID
                  : interval.next_application_item_id ?? "",
              next_application_search_text: "",
              dose_interval_value: interval.dose_interval_value !== null ? String(interval.dose_interval_value) : "",
              dose_interval_unit: (interval.dose_interval_unit as DoseIntervalUnit | null) ?? "",
            })),
        );
        if (existingPackageTemplate) {
          setPackageControlsSessions(existingPackageTemplate.controls_sessions);
          setPackageDefaultSessions(String(existingPackageTemplate.default_sessions));
          setPackageItems(
            existingPackageTemplate.items.length > 0
              ? existingPackageTemplate.items.map((templateItem) => ({
                itemId: templateItem.item_id ?? "",
                itemNameSnapshot: templateItem.item_name_snapshot,
                unitPriceSnapshot: String(templateItem.unit_price_snapshot ?? 0),
              }))
              : [
                {
                  itemId: item.id,
                  itemNameSnapshot: item.name,
                unitPriceSnapshot: String(item.sale_price ?? ""),
              },
            ],
          );
          const baseItem = existingPackageTemplate.items.find((templateItem) => Boolean(templateItem.item_id));
          setStayPackageBaseServiceId(baseItem?.item_id ?? null);
          setStayPackageKind(
            existingStayPackageConfig?.packageKind === "recurring_plan" ? "recurring_plan" : "session_block",
          );
          setStayPackagePeriod(
            existingStayPackageConfig?.period === "weekly" || existingStayPackageConfig?.period === "monthly"
              ? existingStayPackageConfig.period
              : null,
          );
          setStayPackageDaysPerWeek(
            existingStayPackageConfig?.daysPerWeek != null ? String(existingStayPackageConfig.daysPerWeek) : "",
          );
        } else {
          setPackageControlsSessions(true);
          setPackageDefaultSessions("1");
          setStayPackageKind("session_block");
          setStayPackageBaseServiceId(null);
          setStayPackagePeriod(null);
          setStayPackageDaysPerWeek("");
          setPackageItems([
            {
              itemId: "",
              itemNameSnapshot: "",
              unitPriceSnapshot: "",
            },
          ]);
        }
      } else {
        // Resetar com valores padrão para criar novo
        form.reset({
          type: "PRODUTO",
          name: "",
          sku: "",
          barcode: "",
          unit: "UN",
          cost_price: 0,
          sale_price: 0,
          description: "",
          is_active: true,
          track_by_batch: false,
          track_by_expiry: false,
          is_perishable: false,
          gtin: "",
          ncm: "",
          cest: "",
          cfop: "",
          cfop_outside_state: "",
          origin_code: "",
          icms_tax_situation: "",
          pis_tax_situation: "",
          cofins_tax_situation: "",
          ipi_tax_situation: "",
          ipi_enquadramento: "",
          ipi_base_calculo: undefined,
          ipi_aliquota: undefined,
          ipi_valor: undefined,
          nfse_service_list_item: "",
          nfse_service_code: "",
          nfse_cnae: "",
          nfse_iss_rate: undefined,
          nfse_service_description: "",
          unidade_tributavel: "",
          valor_total_tributos: undefined,
          aliquota_aproximada_tributos: undefined,
          fonte_valor_total_tributos: "",
          icms_base_calculo: undefined,
          icms_aliquota: undefined,
          icms_valor: undefined,
          icms_modalidade_base_calculo: "",
          pis_base_calculo: undefined,
          pis_aliquota: undefined,
          pis_valor: undefined,
          cofins_base_calculo: undefined,
          cofins_aliquota: undefined,
          cofins_valor: undefined,
          ibs_cbs_situacao_tributaria: "",
          ibs_cbs_classificacao_tributaria: "",
          ibs_cbs_base_calculo: undefined,
          cbs_aliquota: undefined,
          cbs_valor: undefined,
          ibs_uf_aliquota: undefined,
          ibs_uf_valor: undefined,
          ibs_mun_aliquota: undefined,
          ibs_mun_valor: undefined,
          ibs_valor_total: undefined,
          is_valor: undefined,
          package_subtype_id: undefined,
          restrict_appointment_types: false,
          allowed_appointment_types: [],
          ...(initialValues ?? {}),
        });
        setDoseIntervalRows([]);
        setPackageControlsSessions(true);
        setPackageDefaultSessions("1");
        setStayPackageKind("session_block");
        setStayPackageBaseServiceId(null);
        setStayPackagePeriod(null);
        setStayPackageDaysPerWeek("");
        setPackageItems([
          {
            itemId: "",
            itemNameSnapshot: "",
            unitPriceSnapshot: "",
          },
        ]);
      }
    } else {
      // Resetar quando o modal fecha
      setPricingDriver(null);
      setIsMarkupUnlocked(false);
      setDoseIntervalRows([]);
      setStayPackageKind("session_block");
      setStayPackageBaseServiceId(null);
      setStayPackagePeriod(null);
      setStayPackageDaysPerWeek("");
      form.reset();
    }
  }, [open, item, form, initialValues, existingPackageTemplate, existingStayPackageConfig, itemFiscalProfile]);

  useEffect(() => {
    if (!open) return;

    setStockQuantityDisplay(String(productStock?.quantity ?? 0));
  }, [open, productStock?.quantity]);


  useEffect(() => {
    if (!open || !item || !itemFiscalProfile) return;

    form.setValue("gtin", itemFiscalProfile.gtin || "");
    form.setValue("ncm", itemFiscalProfile.ncm || "");
    form.setValue("cest", itemFiscalProfile.cest || "");
    form.setValue("cfop", itemFiscalProfile.cfop || "");
    form.setValue("cfop_outside_state", itemFiscalProfile.cfop_outside_state || "");
    form.setValue("origin_code", itemFiscalProfile.origin_code || "");
    form.setValue("icms_tax_situation", itemFiscalProfile.icms_tax_situation || "");
    form.setValue("pis_tax_situation", itemFiscalProfile.pis_tax_situation || "");
    form.setValue("cofins_tax_situation", itemFiscalProfile.cofins_tax_situation || "");
    form.setValue("ipi_tax_situation", itemFiscalProfile.ipi_tax_situation || "");
    form.setValue("ipi_enquadramento", itemFiscalProfile.ipi_enquadramento || "");
    form.setValue("ipi_base_calculo", itemFiscalProfile.ipi_base_calculo ?? undefined);
    form.setValue("ipi_aliquota", itemFiscalProfile.ipi_aliquota ?? undefined);
    form.setValue("ipi_valor", itemFiscalProfile.ipi_valor ?? undefined);
    form.setValue("nfse_service_list_item", itemFiscalProfile.nfse_service_list_item || "");
    form.setValue("nfse_service_code", itemFiscalProfile.nfse_service_code || "");
    form.setValue("nfse_cnae", itemFiscalProfile.nfse_cnae || "");
    form.setValue("nfse_iss_rate", itemFiscalProfile.nfse_iss_rate ?? undefined);
    form.setValue("nfse_service_description", itemFiscalProfile.nfse_service_description || "");
    form.setValue("unidade_tributavel", itemFiscalProfile.unidade_tributavel || "");
    form.setValue("valor_total_tributos", itemFiscalProfile.valor_total_tributos ?? undefined);
    form.setValue("aliquota_aproximada_tributos", itemFiscalProfile.aliquota_aproximada_tributos ?? undefined);
    form.setValue("fonte_valor_total_tributos", itemFiscalProfile.fonte_valor_total_tributos || "");
    form.setValue("icms_base_calculo", itemFiscalProfile.icms_base_calculo ?? undefined);
    form.setValue("icms_aliquota", itemFiscalProfile.icms_aliquota ?? undefined);
    form.setValue("icms_valor", itemFiscalProfile.icms_valor ?? undefined);
    form.setValue("icms_modalidade_base_calculo", itemFiscalProfile.icms_modalidade_base_calculo || "");
    form.setValue("pis_base_calculo", itemFiscalProfile.pis_base_calculo ?? undefined);
    form.setValue("pis_aliquota", itemFiscalProfile.pis_aliquota ?? undefined);
    form.setValue("pis_valor", itemFiscalProfile.pis_valor ?? undefined);
    form.setValue("cofins_base_calculo", itemFiscalProfile.cofins_base_calculo ?? undefined);
    form.setValue("cofins_aliquota", itemFiscalProfile.cofins_aliquota ?? undefined);
    form.setValue("cofins_valor", itemFiscalProfile.cofins_valor ?? undefined);
    form.setValue("ibs_cbs_situacao_tributaria", itemFiscalProfile.ibs_cbs_situacao_tributaria || "");
    form.setValue("ibs_cbs_classificacao_tributaria", itemFiscalProfile.ibs_cbs_classificacao_tributaria || "");
    form.setValue("ibs_cbs_base_calculo", itemFiscalProfile.ibs_cbs_base_calculo ?? undefined);
    form.setValue("cbs_aliquota", itemFiscalProfile.cbs_aliquota ?? undefined);
    form.setValue("cbs_valor", itemFiscalProfile.cbs_valor ?? undefined);
    form.setValue("ibs_uf_aliquota", itemFiscalProfile.ibs_uf_aliquota ?? undefined);
    form.setValue("ibs_uf_valor", itemFiscalProfile.ibs_uf_valor ?? undefined);
    form.setValue("ibs_mun_aliquota", itemFiscalProfile.ibs_mun_aliquota ?? undefined);
    form.setValue("ibs_mun_valor", itemFiscalProfile.ibs_mun_valor ?? undefined);
    form.setValue("ibs_valor_total", itemFiscalProfile.ibs_valor_total ?? undefined);
    form.setValue("is_valor", itemFiscalProfile.is_valor ?? undefined);
  }, [open, item, itemFiscalProfile, form]);

  const onSubmit = async (data: CatalogItemInput) => {
    try {
      const toNullable = (value?: string | null) => {
        if (typeof value !== "string") return null;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
      };

      const {
        gtin,
        ncm,
        cest,
        cfop,
        cfop_outside_state,
        origin_code,
        icms_tax_situation,
        pis_tax_situation,
        cofins_tax_situation,
        ipi_tax_situation,
        ipi_enquadramento,
        ipi_base_calculo,
        ipi_aliquota,
        ipi_valor,
        nfse_service_list_item,
        nfse_service_code,
        nfse_cnae,
        nfse_iss_rate,
        nfse_service_description,
        unidade_tributavel,
        valor_total_tributos,
        aliquota_aproximada_tributos,
        fonte_valor_total_tributos,
        icms_base_calculo,
        icms_aliquota,
        icms_valor,
        icms_modalidade_base_calculo,
        pis_base_calculo,
        pis_aliquota,
        pis_valor,
        cofins_base_calculo,
        cofins_aliquota,
        cofins_valor,
        ibs_cbs_situacao_tributaria,
        ibs_cbs_classificacao_tributaria,
        ibs_cbs_base_calculo,
        cbs_aliquota,
        cbs_valor,
        ibs_uf_aliquota,
        ibs_uf_valor,
        ibs_mun_aliquota,
        ibs_mun_valor,
        ibs_valor_total,
        is_valor,
        ...itemDataRaw
      } = data;

      // Garantir que type está presente (sempre deve estar devido ao default no schema)
      const { restrict_appointment_types, ...catalogData } = itemDataRaw;
      const incompleteRow = shouldShowDoseInterval
        ? doseIntervalRows.find((row) => {
            const hasApplication = row.next_application_item_id !== "";
            const hasValue = row.dose_interval_value !== "";
            const hasUnit = row.dose_interval_unit !== "";

            return (hasValue && !hasUnit) || (hasApplication && !hasValue);
          })
        : undefined;
      if (incompleteRow) {
        toast.error(t("erp:itemForm.doseInterval.incompleteRowError"));
        return;
      }

      const inputData = {
        ...catalogData,
        type: catalogData.type || "PRODUTO",
        product_subtype_id: (catalogData.type || "PRODUTO") === "PACOTE" ? null : catalogData.product_subtype_id,
        package_subtype_id: (catalogData.type || "PRODUTO") === "PACOTE" ? catalogData.package_subtype_id : null,
        service_group_id: (catalogData.type || "PRODUTO") === "SERVICO" ? (catalogData.service_group_id ?? null) : null,
        barcode: (catalogData.type || "PRODUTO") === "PACOTE" ? null : catalogData.barcode,
        allowed_appointment_types: restrict_appointment_types ? catalogData.allowed_appointment_types : [],
      } as ItemInput;
      let effectiveItemId = item?.id ?? null;
      const effectiveType = inputData.type || "PRODUTO";

      let persistedItemId: string | undefined = item?.id;
      let stayPackageConfig: StayPackageConfigValue | null = null;
      let stayPackageTemplateItem: ReturnType<typeof buildDaycarePackageTemplateItem> | null = null;

      if (effectiveType === "PACOTE") {
        const sessions = Math.max(1, Number(packageDefaultSessions.replace(",", ".")) || 1);
        const configResult = daycarePackageFormSchema.safeParse({
          packageKind: stayPackageKind,
          baseServiceId: stayPackageBaseServiceId,
          sessions,
          period: stayPackageKind === "recurring_plan" ? stayPackagePeriod : null,
          daysPerWeek: stayPackageKind === "recurring_plan"
            ? (stayPackageDaysPerWeek.trim()
              ? Math.max(1, Number(stayPackageDaysPerWeek.replace(",", ".")) || 0)
              : null)
            : null,
        });

        if (!configResult.success || !selectedDaycareService) {
          toast.error(t("erp:itemForm.package.daycareConfigurationRequired"));
          return;
        }

        stayPackageConfig = toStayPackageConfigValue(
          configResult.data.packageKind,
          configResult.data.period,
          configResult.data.daysPerWeek,
        );
        stayPackageTemplateItem = buildDaycarePackageTemplateItem(selectedDaycareService);
      }

      if (item) {
        await updateItem.mutateAsync({
          id: item.id,
          input: inputData,
        });
      } else {
        const newItem = await createItem.mutateAsync(inputData);
        persistedItemId = newItem?.id;
        effectiveItemId = newItem?.id ?? null;
        if (onProductCreated && newItem?.id) {
          onProductCreated(newItem.id);
        }
      }

      if (persistedItemId && effectiveType === "PRODUTO") {
        const stockQuantity = parseLocaleNumber(stockQuantityDisplay);
        await addOrUpdateStock.mutateAsync({
          item_id: persistedItemId,
          quantity: Number.isFinite(stockQuantity) ? stockQuantity : 0,
          adjustment_reason: t("erp:itemForm.stock.adjustmentReason"),
        });
      }

      const toNum = (v: unknown): number | null =>
        typeof v === "number" && Number.isFinite(v) ? v : null;

      if (persistedItemId && fiscalEnabled) {
        await upsertItemFiscalProfile.mutateAsync({
          item_id: persistedItemId,
          gtin: toNullable(gtin),
          ncm: toNullable(ncm),
          cest: toNullable(cest),
          cfop: toNullable(cfop),
          cfop_outside_state: toNullable(cfop_outside_state),
          origin_code: toNullable(origin_code),
          icms_tax_situation: toNullable(icms_tax_situation),
          pis_tax_situation: toNullable(pis_tax_situation),
          cofins_tax_situation: toNullable(cofins_tax_situation),
          ipi_tax_situation: toNullable(ipi_tax_situation),
          ipi_enquadramento: toNullable(ipi_enquadramento),
          ipi_base_calculo: toNum(ipi_base_calculo),
          ipi_aliquota: toNum(ipi_aliquota),
          ipi_valor: toNum(ipi_valor),
          nfse_service_list_item: toNullable(nfse_service_list_item),
          nfse_service_code: toNullable(nfse_service_code),
          nfse_cnae: toNullable(nfse_cnae),
          nfse_iss_rate:
            typeof nfse_iss_rate === "number" && Number.isFinite(nfse_iss_rate)
              ? nfse_iss_rate
              : null,
          nfse_service_description: toNullable(nfse_service_description),
          unidade_tributavel: toNullable(unidade_tributavel),
          valor_total_tributos: toNum(valor_total_tributos),
          aliquota_aproximada_tributos: toNum(aliquota_aproximada_tributos),
          fonte_valor_total_tributos: toNullable(fonte_valor_total_tributos),
          icms_base_calculo: toNum(icms_base_calculo),
          icms_aliquota: toNum(icms_aliquota),
          icms_valor: toNum(icms_valor),
          icms_modalidade_base_calculo: toNullable(icms_modalidade_base_calculo),
          pis_base_calculo: toNum(pis_base_calculo),
          pis_aliquota: toNum(pis_aliquota),
          pis_valor: toNum(pis_valor),
          cofins_base_calculo: toNum(cofins_base_calculo),
          cofins_aliquota: toNum(cofins_aliquota),
          cofins_valor: toNum(cofins_valor),
          ibs_cbs_situacao_tributaria: toNullable(ibs_cbs_situacao_tributaria),
          ibs_cbs_classificacao_tributaria: toNullable(ibs_cbs_classificacao_tributaria),
          ibs_cbs_base_calculo: toNum(ibs_cbs_base_calculo),
          cbs_aliquota: toNum(cbs_aliquota),
          cbs_valor: toNum(cbs_valor),
          ibs_uf_aliquota: toNum(ibs_uf_aliquota),
          ibs_uf_valor: toNum(ibs_uf_valor),
          ibs_mun_aliquota: toNum(ibs_mun_aliquota),
          ibs_mun_valor: toNum(ibs_mun_valor),
          ibs_valor_total: toNum(ibs_valor_total),
          is_valor: toNum(is_valor),
        });
      }

      if (persistedItemId) {
        const validRows = shouldShowDoseInterval
          ? doseIntervalRows
              .filter((row) => row.dose_interval_value !== "" && row.dose_interval_unit !== "")
              .map((row, index) => ({
                id: row.id,
                next_application_item_id:
                  row.next_application_item_id === CURRENT_ITEM_NEXT_APPLICATION_ID
                    ? persistedItemId
                    : row.next_application_item_id || null,
                dose_interval_value: Number(row.dose_interval_value),
                dose_interval_unit: row.dose_interval_unit as DoseIntervalUnit,
                sort_order: index,
              }))
          : [];
        await upsertItemDoseIntervals.mutateAsync({ itemId: persistedItemId, intervals: validRows });
      }


      if (isPackageTypeSelected && effectiveItemId) {
        if (!stayPackageConfig || !stayPackageTemplateItem) return;

        const sessions = Math.max(1, Number(packageDefaultSessions.replace(",", ".")) || 1);
        const defaultSessions = stayPackageConfig.packageKind === "session_block"
          ? sessions
          : Math.max(1, stayPackageConfig.daysPerWeek ?? 1);
        const templateId = await upsertCatalogPackageTemplate.mutateAsync({
          itemId: effectiveItemId,
          chargeMode: "antecipado",
          controlsSessions: true,
          defaultSessions,
          notes: data.description ?? null,
          items: [stayPackageTemplateItem],
        });
        await upsertStayPackageConfig.mutateAsync({
          catalogPackageId: templateId,
          config: stayPackageConfig,
        });
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error submitting item:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] min-h-0 max-w-3xl">
        <DialogHeader>
          <DialogTitle>{item ? t("erp:itemForm.titleEdit") : t("erp:itemForm.titleNew")}</DialogTitle>
          <DialogDescription>
            {item ? t("erp:itemForm.descriptionEdit") : t("erp:itemForm.descriptionNew")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {(() => {
              const generalTabContent = (
                <section className="rounded-lg border border-border/60 bg-white p-4">
              <p className="text-sm font-semibold text-foreground mb-4">{t("erp:itemForm.sections.general")}</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.type")}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value ?? ""}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t("erp:itemForm.placeholders.type")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="PRODUTO">{t("erp:common.product")}</SelectItem>
                              <SelectItem value="SERVICO">{t("erp:common.service")}</SelectItem>
                              <SelectItem value="PACOTE">{t("erp:common.package")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                    )}
                  />

                  {(itemType === "PRODUTO" || itemType === "PACOTE" || itemType === "SERVICO") && (
                    <FormField
                      control={form.control}
                      name={
                        itemType === "PACOTE"
                          ? "package_subtype_id"
                          : itemType === "SERVICO"
                            ? "service_group_id"
                            : "product_subtype_id"
                      }
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.subtype")}</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t("erp:itemForm.placeholders.subtype")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {(itemType === "PACOTE" ? packageSubtypes : itemType === "SERVICO" ? serviceGroups : productSubtypes).map((st) => (
                                <SelectItem key={st.id} value={st.id}>
                                  {st.name.charAt(0).toUpperCase() + st.name.slice(1)}
                                </SelectItem>
                              ))}
                              <SelectSeparator />
                              <div className="p-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                  onClick={() =>
                                    itemType === "PACOTE"
                                      ? setCreatePackageSubtypeDialogOpen(true)
                                      : itemType === "SERVICO"
                                        ? setCreateServiceGroupDialogOpen(true)
                                        : setCreateProductSubtypeDialogOpen(true)
                                  }
                                >
                                  {itemType === "SERVICO"
                                    ? t("erp:itemForm.createServiceGroup")
                                    : t("erp:itemForm.createSubtype")}
                                </Button>
                              </div>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                {itemType === "PACOTE" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.nameRequired")}</FormLabel>
                          <FormControl>
                            <Input placeholder={namePlaceholder} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sku"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{skuLabel}</FormLabel>
                          <FormControl>
                            <Input placeholder={skuPlaceholder} {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                        <FormLabel>{t("erp:itemForm.labels.nameRequired")}</FormLabel>
                        <FormControl>
                          <Input placeholder={namePlaceholder} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className={itemType === "PACOTE" ? "grid grid-cols-1 gap-4" : "grid grid-cols-2 gap-4"}>
                  {itemType !== "PACOTE" ? (
                    <FormField
                      control={form.control}
                      name="sku"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{skuLabel}</FormLabel>
                          <FormControl>
                            <Input placeholder={skuPlaceholder} {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  {itemType === "PRODUTO" ? (
                    <FormField
                      control={form.control}
                      name="barcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.barcode")}</FormLabel>
                          <div className="flex gap-2">
                            <FormControl>
                              <Input placeholder={t("erp:itemForm.placeholders.barcode")} {...field} value={field.value || ""} />
                            </FormControl>
                            <BarcodeScanner
                              onScan={(barcode) => form.setValue("barcode", barcode)}
                              buttonSize="default"
                            />
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("erp:itemForm.labels.description")}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={descriptionPlaceholder}
                          className="resize-none"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {shouldShowDoseInterval ? (
                  <div className="rounded-lg border border-border p-4">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{t("erp:itemForm.labels.doseInterval")}</p>
                        <p className="text-sm text-muted-foreground">{t("erp:itemForm.doseInterval.description")}</p>
                      </div>

                      {doseIntervalRows.length > 0 && (
                        <div className="space-y-2">
                          {doseIntervalRows.map((row, index) => (
                            <div
                              key={`dose-interval-row-${index}`}
                              className="grid gap-2 rounded-md border p-3 md:grid-cols-[minmax(0,1fr)_120px_120px_auto]"
                            >
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("erp:itemForm.doseInterval.nextApplicationLabel")}</Label>
                                <Autocomplete
                                  options={nextApplicationOptions}
                                  value={row.next_application_search_text ?? getNextApplicationDisplayText(row.next_application_item_id)}
                                  selectedValue={row.next_application_item_id}
                                  onChange={(value) =>
                                    setDoseIntervalRows((prev) =>
                                      prev.map((r, i) => i === index ? { ...r, next_application_search_text: value } : r)
                                    )
                                  }
                                  onSelect={(option) =>
                                    setDoseIntervalRows((prev) =>
                                      prev.map((r, i) =>
                                        i === index
                                          ? {
                                            ...r,
                                            next_application_item_id: option.value,
                                            next_application_search_text:
                                              option.value === CURRENT_ITEM_NEXT_APPLICATION_ID ? undefined : option.label,
                                          }
                                          : r
                                      )
                                    )
                                  }
                                  placeholder={t("erp:itemForm.doseInterval.nextApplicationPlaceholder")}
                                  emptyMessage={t("erp:itemForm.doseInterval.nextApplicationEmpty")}
                                  disableLocalFilter
                                  openOnFocus
                                  openOnClick
                                />
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("erp:itemForm.doseInterval.valueLabel")}</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  inputMode="numeric"
                                  placeholder={t("erp:itemForm.placeholders.doseIntervalValue")}
                                  value={row.dose_interval_value}
                                  onChange={(e) =>
                                    setDoseIntervalRows((prev) =>
                                      prev.map((r, i) => i === index ? { ...r, dose_interval_value: e.target.value } : r)
                                    )
                                  }
                                />
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("erp:itemForm.doseInterval.unitLabel")}</Label>
                                <Select
                                  value={row.dose_interval_unit}
                                  onValueChange={(value) =>
                                    setDoseIntervalRows((prev) =>
                                      prev.map((r, i) => i === index ? { ...r, dose_interval_unit: value as DoseIntervalUnit } : r)
                                    )
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t("erp:itemForm.placeholders.doseIntervalUnit")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DOSE_INTERVAL_UNITS.map((unit) => (
                                      <SelectItem key={unit} value={unit}>
                                        {t(`erp:itemForm.doseInterval.units.${unit}`)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="flex items-end justify-end">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setDoseIntervalRows((prev) => prev.filter((_, i) => i !== index))
                                  }
                                  aria-label={t("erp:itemForm.doseInterval.removeRowAria")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setDoseIntervalRows((prev) => [
                            ...prev,
                            {
                              next_application_item_id: CURRENT_ITEM_NEXT_APPLICATION_ID,
                              dose_interval_value: "",
                              dose_interval_unit: DOSE_INTERVAL_UNITS[0],
                            },
                          ])
                        }
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("erp:itemForm.doseInterval.addInterval")}
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="rounded-lg border border-border p-4">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="restrict_appointment_types"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between gap-3 space-y-0">
                          <FormLabel className="font-medium">
                            {t("erp:itemForm.appointmentAvailability.showInAll")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={!field.value}
                              onCheckedChange={(checked) => field.onChange(!checked)}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {restrictAppointmentTypes ? (
                      <FormField
                        control={form.control}
                        name="allowed_appointment_types"
                        render={({ field }) => (
                          <FormItem>
                            <FormDescription>
                              {t("erp:itemForm.appointmentAvailability.restrictDescription", {
                                itemType: availabilityItemLabel.toLowerCase(),
                              })}
                            </FormDescription>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {BEHAVIOR_OPTIONS.map((option) => {
                                const checked = field.value?.includes(option.value) ?? false;
                                return (
                                  <label
                                    key={option.value}
                                    className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm"
                                  >
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(nextChecked) => {
                                        const currentValue = field.value ?? [];
                                        field.onChange(
                                          nextChecked
                                            ? [...currentValue, option.value]
                                            : currentValue.filter((value) => value !== option.value),
                                        );
                                      }}
                                    />
                                    <span>{t(`calendar:${option.labelKey}`)}</span>
                                  </label>
                                );
                              })}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>
                </div>

                {/* Ativo/Inativo */}
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">{t("erp:itemForm.labels.active")}</FormLabel>
                        <FormDescription>
                          {t("erp:itemForm.activeDescription")}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </section>
              );

              /* ── Tab: Preço ── */
              const priceTabContent = (
                <>
            {itemType === "PRODUTO" && !isPackageTypeSelected && (
              <section className="rounded-lg border border-border/60 bg-white p-4">
                <p className="text-sm font-semibold text-foreground mb-4">{t("erp:itemForm.sections.prices")}</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="unit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.unit")}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value ?? ""}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="UN">{t("erp:itemForm.units.un")}</SelectItem>
                              <SelectItem value="KG">{t("erp:itemForm.units.kg")}</SelectItem>
                              <SelectItem value="L">{t("erp:itemForm.units.l")}</SelectItem>
                              <SelectItem value="ML">{t("erp:itemForm.units.ml")}</SelectItem>
                              <SelectItem value="MG">{t("erp:itemForm.units.mg")}</SelectItem>
                              <SelectItem value="CX">{t("erp:itemForm.units.cx")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="cost_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.costPrice")}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder={t("erp:itemForm.placeholders.currency")}
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => {
                                const parsed = parseLocaleNumber(e.target.value);
                                field.onChange(Number.isFinite(parsed) ? parsed : 0);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="markup_percentage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.markup")}</FormLabel>
                          <div className="relative">
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                inputMode="decimal"
                                placeholder={t("erp:itemForm.placeholders.currency")}
                                className="text-sm"
                                disabled={!isMarkupUnlocked}
                                {...field}
                                value={
                                  !isMarkupUnlocked
                                    ? computedMarkupPercentage === undefined
                                      ? ""
                                      : computedMarkupPercentage.toFixed(2)
                                    : (field.value ?? "")
                                }
                                onChange={(e) => {
                                  setPricingDriver("markup");
                                  const parsed = parseLocaleNumber(e.target.value);
                                  field.onChange(Number.isFinite(parsed) ? parsed : undefined);
                                }}
                              />
                            </FormControl>
                            {isMarkupUnlocked ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full"
                                onClick={toggleMarkupUnlocked}
                                aria-label={t("erp:itemForm.markup.lock")}
                                title={t("erp:itemForm.markup.lock")}
                              >
                                <Unlock className="h-4 w-4" />
                              </Button>
                            ) : (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full"
                                  onClick={toggleMarkupUnlocked}
                                  aria-label={t("erp:itemForm.markup.unlock")}
                                >
                                  <Lock className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" align="end">
                                {t("erp:itemForm.markup.unlockHint")}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                    name="sale_price"
                    render={({ field }) => (
                      <FormItem>
                          <FormLabel>{t("erp:itemForm.labels.salePrice")}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder={t("erp:itemForm.placeholders.currency")}
                              disabled={isMarkupUnlocked}
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => {
                                setPricingDriver("sale");
                                const parsed = parseLocaleNumber(e.target.value);
                                field.onChange(Number.isFinite(parsed) ? parsed : 0);
                              }}
                            />
                          </FormControl>
                          {isMarkupUnlocked && (
                            <FormDescription className="text-xs text-color-light">{t("erp:itemForm.markup.lockedDescription")}</FormDescription>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* SERVICO: sale price in Preço tab */}
            {itemType === "SERVICO" && (
              <section className="rounded-lg border border-border/60 bg-white p-4">
                <p className="text-sm font-semibold text-foreground mb-4">{t("erp:itemForm.sections.prices")}</p>
                <FormField
                  control={form.control}
                  name="sale_price"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <FormLabel>{t("erp:itemForm.labels.salePriceRequired")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t("erp:itemForm.placeholders.currency")}
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            setPricingDriver("sale");
                            const parsed = parseLocaleNumber(e.target.value);
                            field.onChange(Number.isFinite(parsed) ? parsed : 0);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </section>
            )}

            {/* PACOTE: daycare package builder in Preço tab */}
            {isPackageTypeSelected && (
              <section className="rounded-lg border border-border/60 bg-white p-4">
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <FormLabel>{t("erp:itemForm.package.stayModuleLabel")}</FormLabel>
                      <Input value={t("erp:itemForm.package.daycareModule")} disabled className="text-foreground" />
                    </div>
                    <div className="space-y-1.5">
                      <FormLabel>{t("erp:itemForm.package.kindLabel")}</FormLabel>
                      <Select
                        value={stayPackageKind}
                        onValueChange={(value: StayPackageConfigValue["packageKind"]) => {
                          setStayPackageKind(value);
                          if (value === "session_block") {
                            setStayPackagePeriod(null);
                            setStayPackageDaysPerWeek("");
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="session_block">{t("erp:itemForm.package.sessionBlock")}</SelectItem>
                          <SelectItem value="recurring_plan">{t("erp:itemForm.package.recurringPlan")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <FormLabel>{t("erp:itemForm.package.baseServiceLabel")}</FormLabel>
                    <Autocomplete
                      popoverPortalled={false}
                      preventParentScrollOnFocus
                      useInlineDropdown
                      openOnFocus={false}
                      options={daycareServiceOptions}
                      value={selectedDaycareService?.name ?? packageItems[0]?.itemNameSnapshot ?? ""}
                      selectedValue={stayPackageBaseServiceId ?? ""}
                      onChange={(value) => {
                        setStayPackageBaseServiceId(null);
                        setPackageItems([{ itemId: "", itemNameSnapshot: value, unitPriceSnapshot: "" }]);
                      }}
                      onSelect={(option) => {
                        const selected = catalogItems.find((catalogItem) => catalogItem.id === option.value);
                        setStayPackageBaseServiceId(option.value);
                        setPackageItems([{ itemId: option.value, itemNameSnapshot: option.label, unitPriceSnapshot: String(selected?.sale_price ?? "") }]);
                      }}
                      usePopoverAnchor
                      placeholder={t("erp:itemForm.package.baseServicePlaceholder")}
                      emptyMessage={t("erp:itemForm.package.baseServiceEmpty")}
                    />
                  </div>

                  <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                    {t("erp:itemForm.package.hotelNotIncludedHint")}
                  </p>
                </div>

                <div className="mt-4 rounded-md border p-4">
                  <p className="mb-3 text-sm font-medium">{t("erp:itemForm.package.pricingTitle")}</p>
                  <div className="grid gap-3 md:grid-cols-3">
                    {stayPackageKind === "session_block" ? (
                      <div className="space-y-1.5">
                        <FormLabel>{t("erp:itemForm.package.defaultSessionsLabel")}</FormLabel>
                        <Input type="text" inputMode="numeric" value={packageDefaultSessions} onChange={(e) => setPackageDefaultSessions(normalizeQuantityInput(e.target.value))} />
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <FormLabel>{t("erp:itemForm.package.periodLabel")}</FormLabel>
                        <Select
                          value={stayPackagePeriod ?? ""}
                          onValueChange={(value: NonNullable<StayPackageConfigValue["period"]>) => setStayPackagePeriod(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t("erp:itemForm.package.periodPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">{t("erp:itemForm.package.weeklyPeriod")}</SelectItem>
                            <SelectItem value="monthly">{t("erp:itemForm.package.monthlyPeriod")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {stayPackageKind === "recurring_plan" && (
                      <div className="space-y-1.5">
                        <FormLabel>{t("erp:itemForm.package.daysPerWeekLabel")}</FormLabel>
                        <Input type="text" inputMode="numeric" value={stayPackageDaysPerWeek} onChange={(e) => setStayPackageDaysPerWeek(normalizeQuantityInput(e.target.value))} />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <FormLabel>{t("erp:itemForm.package.referenceValueLabel")}</FormLabel>
                      <Input value={computedPackagePrice.toFixed(2)} disabled className="text-foreground" />
                    </div>
                    <FormField
                      control={form.control}
                      name="sale_price"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5">
                          <FormLabel>{t("erp:itemForm.package.finalValueLabel")}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder={t("erp:itemForm.placeholders.currency")}
                              value={field.value == null ? "" : String(field.value)}
                              onChange={(e) => {
                                const normalized = normalizeCurrencyInput(e.target.value);
                                const parsed = parseCurrencyInput(normalized);
                                field.onChange(Number.isFinite(parsed) ? parsed : 0);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </section>
            )}

                </>
              );

              /* ── Tab: Estoque ── */
              const stockTabContent = (
                <section className="rounded-lg border border-border/60 bg-white p-4">
                  <p className="text-sm font-semibold text-foreground mb-4">{t("erp:itemForm.sections.tabs.stock")}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormItem>
                      <FormLabel>{t("erp:itemForm.stock.currentQuantity")}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="decimal"
                          value={stockQuantityDisplay}
                          onChange={(event) => setStockQuantityDisplay(normalizeQuantityInput(event.target.value))}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {t("erp:itemForm.stock.alwaysControlledDescription")}
                      </FormDescription>
                    </FormItem>
                    <FormItem>
                      <FormLabel>{t("erp:itemForm.stock.location")}</FormLabel>
                      <FormControl>
                        <Input value={t("erp:itemForm.stock.defaultLocation")} disabled readOnly />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {t("erp:itemForm.stock.locationDescription")}
                      </FormDescription>
                    </FormItem>
                  </div>
                </section>
              );

              /* ── Tab: Fiscal ── */
              const fiscalTabContent = (
                <>
            {fiscalEnabled && itemType === "PRODUTO" && (
              <section className="rounded-lg border border-border/60 bg-white p-4 space-y-5">
                <p className="text-sm font-semibold text-foreground">{t("erp:itemForm.fiscal.nfce.title")}</p>

                {/* ── Identificação fiscal ── */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    {t("erp:itemForm.fiscal.nfce.identification")}
                  </p>
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="gtin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.gtin")}</FormLabel>
                          <FormControl>
                            <Input placeholder={t("erp:itemForm.fiscal.placeholders.gtin")} {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="ncm"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.ncm")}</FormLabel>
                          <FocusTaxCodeAutocomplete
                            type="ncm"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t("erp:itemForm.fiscal.placeholders.ncm")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cest"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.cest")}</FormLabel>
                          <FormControl>
                            <Input placeholder={t("erp:itemForm.fiscal.placeholders.cest")} {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cfop"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.cfopIntra")}</FormLabel>
                          <FocusTaxCodeAutocomplete
                            type="cfop"
                            mode="intra"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t("erp:itemForm.fiscal.placeholders.cfop")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cfop_outside_state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.cfopInter")}</FormLabel>
                          <FocusTaxCodeAutocomplete
                            type="cfop"
                            mode="inter"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t("erp:itemForm.fiscal.placeholders.cfop")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="origin_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.origin")}</FormLabel>
                          <FiscalCombobox
                            value={field.value}
                            onChange={field.onChange}
                            options={originOptions}
                            placeholder={t("erp:itemForm.fiscal.placeholders.origin")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="icms_tax_situation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.icmsSituation")}</FormLabel>
                          <FiscalCombobox
                            value={field.value}
                            onChange={field.onChange}
                            options={icmsOptions}
                            placeholder={t("erp:itemForm.fiscal.placeholders.icms")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="pis_tax_situation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.pisSituation")}</FormLabel>
                          <FiscalCombobox
                            value={field.value}
                            onChange={field.onChange}
                            options={pisCofinsOptions}
                            placeholder={t("erp:itemForm.fiscal.placeholders.pisCofins")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cofins_tax_situation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.cofinsSituation")}</FormLabel>
                          <FiscalCombobox
                            value={field.value}
                            onChange={field.onChange}
                            options={pisCofinsOptions}
                            placeholder={t("erp:itemForm.fiscal.placeholders.pisCofins")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="unidade_tributavel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.taxableUnit")}</FormLabel>
                          <FiscalCombobox
                            value={field.value}
                            onChange={field.onChange}
                            options={unitOptions}
                            placeholder={t("erp:itemForm.fiscal.placeholders.taxableUnit")}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField control={form.control} name="valor_total_tributos" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("erp:itemForm.fiscal.labels.approximateTributesValue")}</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="aliquota_aproximada_tributos" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("erp:itemForm.fiscal.labels.approximateTributesRate")}</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.0001" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField
                      control={form.control}
                      name="fonte_valor_total_tributos"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.approximateTributesSource")}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t("erp:itemForm.fiscal.placeholders.approximateTributesSource")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="imported_xml">{t("erp:itemForm.fiscal.tributeSources.importedXml")}</SelectItem>
                              <SelectItem value="ibpt">{t("erp:itemForm.fiscal.tributeSources.ibpt")}</SelectItem>
                              <SelectItem value="manual">{t("erp:itemForm.fiscal.tributeSources.manual")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* ── Impostos ── */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    {t("erp:itemForm.fiscal.nfce.taxes")}
                  </p>
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <FormField control={form.control} name="icms_base_calculo" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.icmsBase")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="icms_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.icmsRate")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="icms_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.icmsValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="pis_base_calculo" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.pisBase")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="pis_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.pisRate")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="pis_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.pisValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="cofins_base_calculo" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.cofinsBase")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="cofins_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.cofinsRate")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="cofins_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.cofinsValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ipi_enquadramento" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ipiEnquadramento")}</FormLabel><FormControl><Input {...field} value={field.value || ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ipi_base_calculo" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ipiBase")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ipi_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ipiRate")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ipi_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ipiValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>

                {/* ── Reforma tributária IBS/CBS ── */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    {t("erp:itemForm.fiscal.nfce.reform")}
                  </p>
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <FormField control={form.control} name="ibs_cbs_situacao_tributaria" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsCbsSituation")}</FormLabel><FiscalCombobox value={field.value} onChange={field.onChange} placeholder={t("erp:itemForm.fiscal.placeholders.ibsCbs")} options={ibsCbsSituationOptions} /><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_cbs_classificacao_tributaria" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsCbsClass")}</FormLabel><FiscalCombobox value={field.value} onChange={field.onChange} placeholder={t("erp:itemForm.fiscal.placeholders.ibsCbs")} options={ibsCbsClassOptions} /><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_cbs_base_calculo" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsCbsBase")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="cbs_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.cbsRate")}</FormLabel><FormControl><Input type="number" step="0.01" placeholder={t("erp:itemForm.fiscal.placeholders.cbsRate")} {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="cbs_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.cbsValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_uf_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsUfRate")}</FormLabel><FormControl><Input type="number" step="0.01" placeholder={t("erp:itemForm.fiscal.placeholders.ibsUfRate")} {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_uf_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsUfValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_mun_aliquota" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsMunRate")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_mun_valor" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsMunValue")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="ibs_valor_total" render={({ field }) => (
                      <FormItem><FormLabel>{t("erp:itemForm.fiscal.labels.ibsTotal")}</FormLabel><FormControl><Input type="number" step="0.01" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>
              </section>
            )}

            {fiscalEnabled && itemType === "SERVICO" && (
              <section className="rounded-lg border border-border/60 bg-white p-4">
                <div className="mb-4 space-y-1">
                  <p className="text-sm font-semibold text-foreground">{t("erp:itemForm.fiscal.nfse.title")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("erp:itemForm.fiscal.nfse.saoPauloProfileDescription")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="nfse_service_list_item"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("erp:itemForm.fiscal.labels.serviceListItem")}</FormLabel>
                        <FocusServiceListAutocomplete
                          municipalityCode={municipalityCode}
                          value={field.value}
                          onChange={(value, taxRate) => {
                            field.onChange(value);
                            if (taxRate !== null) {
                              form.setValue("nfse_iss_rate", taxRate, { shouldDirty: true });
                            }
                          }}
                          placeholder={t("erp:itemForm.fiscal.placeholders.serviceListItem")}
                          disabled={form.formState.isSubmitting}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="nfse_iss_rate"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-1">
                          <FormLabel>{t("erp:itemForm.fiscal.labels.issRate")}</FormLabel>
                        </div>
                        <FormControl className="mt-2">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={t("erp:itemForm.fiscal.placeholders.issRate")}
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const parsed = parseLocaleNumber(e.target.value);
                              field.onChange(Number.isFinite(parsed) ? parsed : undefined);
                            }}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-color-light">{t("erp:itemForm.fiscal.nfse.help.issRate")}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {isNfseServiceCodeSupported && (
                    <FormField
                      control={form.control}
                      name="nfse_service_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.serviceCode")}</FormLabel>
                          <FormControl>
                            <Input placeholder={t("erp:itemForm.fiscal.placeholders.serviceCode")} {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage  />
                        </FormItem>
                      )}
                    />
                  )}
                  {isNfseCnaeSupported && (
                    <FormField
                      control={form.control}
                      name="nfse_cnae"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("erp:itemForm.fiscal.labels.cnae")}</FormLabel>
                          <FocusCnaeAutocomplete
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t("erp:itemForm.fiscal.placeholders.cnae")}
                            disabled={form.formState.isSubmitting}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  <FormField
                    control={form.control}
                    name="nfse_service_description"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>{t("erp:itemForm.fiscal.nfse.labels.serviceDescription")}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t("erp:itemForm.fiscal.nfse.placeholders.serviceDescription")}
                            className="resize-none"
                            rows={3}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-color-light">{t("erp:itemForm.fiscal.nfse.help.serviceDescription")}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </section>
            )}

                </>
              );

              return (
                <SegmentedTabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  fill="screen"
                  items={[
                    { value: "general", label: <>{t("erp:itemForm.sections.tabs.general")}</>, content: generalTabContent },
                    // Packages always require a sale price (validated in catalogItemSchema),
                    // so their price tab stays regardless of the financial module.
                    ...(hasFinancial || itemType === "PACOTE"
                      ? [{ value: "price", label: <>{t("erp:itemForm.sections.tabs.price")}</>, content: priceTabContent }]
                      : []),
                    ...(hasStock && itemType === "PRODUTO"
                      ? [{ value: "stock", label: <>{t("erp:itemForm.sections.tabs.stock")}</>, content: stockTabContent }]
                      : []),
                    ...(fiscalEnabled
                      ? [{ value: "fiscal", label: <>{t("erp:itemForm.sections.tabs.fiscal")}</>, content: fiscalTabContent }]
                      : []),
                  ]}
                />
              );
            })()}

            <DialogFooter className="sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  form.reset();
                }}
                className="gap-2"
              >
                <CustomIcons name="cancel-circle" className="!h-5 !w-5 !text-color-dark" />
                {t("common:cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createItem.isPending || updateItem.isPending || addOrUpdateStock.isPending || upsertCatalogPackageTemplate.isPending || upsertItemDoseIntervals.isPending}
              >
                <CustomIcons name="check-circle" className="!h-5 !w-5 !text-primary-foreground" />
                {createItem.isPending || updateItem.isPending || addOrUpdateStock.isPending || upsertCatalogPackageTemplate.isPending
                  ? t("erp:itemForm.saving")
                  : item
                    ? t("erp:itemForm.submitUpdate")
                    : t("erp:itemForm.submitCreate")}
              </Button>
            </DialogFooter>
          </form>
        </Form>

        <CreateProductSubtypeDialog
          open={createProductSubtypeDialogOpen}
          onOpenChange={setCreateProductSubtypeDialogOpen}
          onSubtypeCreated={(subtype) => {
            form.setValue("product_subtype_id", subtype.id);
          }}
        />
        <CreateServiceGroupDialog
          open={createServiceGroupDialogOpen}
          onOpenChange={setCreateServiceGroupDialogOpen}
          onGroupCreated={(group) => form.setValue("service_group_id", group.id)}
        />
        <CreatePackageSubtypeDialog
          open={createPackageSubtypeDialogOpen}
          onOpenChange={setCreatePackageSubtypeDialogOpen}
          onSubtypeCreated={(subtype) => {
            form.setValue("package_subtype_id", subtype.id);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
